import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { BedDouble, Bath, Car, Ruler, MapPin, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getImageUrl } from "@/lib/storage";
import { formatPrice, type Currency } from "@/lib/format";
import { useI18n } from "@/lib/i18n";
import { localized } from "@/lib/seo";

export type PropertyCardData = {
  id: string;
  slug: string;
  title: string;
  title_en?: string | null;
  title_pt?: string | null;
  operation: string;
  property_type: string;
  price_amount: number;
  price_currency: string;
  address_text: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  garage_spots: number | null;
  area_m2: number | null;
  is_featured: boolean;
  is_opportunity: boolean;
  amenities?: string[] | null;
  cover_url?: string | null;
};

export function PropertyCard({ p }: { p: PropertyCardData }) {
  const [cover, setCover] = useState<string>("");
  const { t, lang } = useI18n();
  const title = localized(p as unknown as Record<string, unknown>, "title", lang) ?? p.title;

  useEffect(() => {
    let alive = true;
    (async () => {
      let path = p.cover_url;
      if (!path) {
        const { data } = await supabase
          .from("property_images")
          .select("url")
          .eq("property_id", p.id)
          .order("sort_order")
          .limit(1)
          .maybeSingle();
        path = data?.url ?? null;
      }
      if (path && alive) setCover(await getImageUrl(path, { width: 640, quality: 72 }));
    })();
    return () => {
      alive = false;
    };
  }, [p.id, p.cover_url]);

  return (
    <Link
      to="/propiedad/$slug"
      params={{ slug: p.slug }}
      className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-soft transition hover:shadow-card"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-secondary">
        {cover ? (
          <img
            src={cover}
            alt={title}
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-[linear-gradient(135deg,oklch(0.35_0.05_215/0.08),oklch(0.72_0.1_85/0.10))]">
            <span className="font-serif text-5xl text-accent/40">D</span>
            <span className="text-xs text-muted-foreground">{t("card.nophoto")}</span>
          </div>
        )}
        <div className="absolute left-3 top-3 flex gap-1.5">
          {p.is_featured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-accent-foreground">
              <Star className="h-3 w-3" /> {t("card.featured")}
            </span>
          )}
          {p.is_opportunity && (
            <span className="rounded-full bg-primary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
              {t("card.opportunity")}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div>
          <div className="text-lg font-semibold text-foreground">
            {formatPrice(p.price_amount, p.price_currency as Currency)}
          </div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {p.operation.replace("_", " ")} · {p.property_type}
          </div>
        </div>

        <h3 className="line-clamp-2 text-sm font-medium text-foreground/90">{title}</h3>

        {p.address_text && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" /> {p.address_text}
          </div>
        )}

        {p.amenities && p.amenities.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {p.amenities.slice(0, 3).map((a) => (
              <span
                key={a}
                className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-secondary-foreground"
              >
                {a}
              </span>
            ))}
            {p.amenities.length > 3 && (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                +{p.amenities.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="mt-auto flex flex-wrap gap-3 border-t border-border pt-3 text-xs text-muted-foreground">
          {p.bedrooms != null && (
            <span className="inline-flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" /> {p.bedrooms}</span>
          )}
          {p.bathrooms != null && (
            <span className="inline-flex items-center gap-1"><Bath className="h-3.5 w-3.5" /> {p.bathrooms}</span>
          )}
          {p.garage_spots != null && p.garage_spots > 0 && (
            <span className="inline-flex items-center gap-1"><Car className="h-3.5 w-3.5" /> {p.garage_spots}</span>
          )}
          {p.area_m2 != null && (
            <span className="inline-flex items-center gap-1"><Ruler className="h-3.5 w-3.5" /> {p.area_m2} m²</span>
          )}
        </div>
      </div>
    </Link>
  );
}
